import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { type Registry as Services, service } from '@ember/service';
import { PrismDataBar } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
// @ts-expect-error: Incorrect type
import preventDefault from 'ember-event-helpers/helpers/prevent-default';
import { t } from 'ember-intl';
import {
  type Platform,
  PLATFORMS,
} from 'frontend-utils/utils/constants/constant-values';
import {
  COLOR_PARITY_LOSS,
  COLOR_PARITY_MEET,
  COLOR_PARITY_WIN,
} from 'frontend-utils/utils/css-variable';
import ParityInsightBreakdownValue from 'lumen/components/parity/insight/breakdown-value';
import ParityTooltipsTooltipKpi from 'lumen/components/parity/tooltips/tooltip-kpi';
import type { HotelViewFilters } from 'lumen/controllers/market/parity/insight';
import roundParityPercentages from 'lumen/utils/parity-analytics/round-parity-percentages';

import styles from './breakdown-value.module.css';

export default class ParityInsightHotelsValue extends ParityInsightBreakdownValue {
  @service declare usageAnalytics: Services['usage-analytics'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get barData() {
    const percentages = this.args.data?.calculatedValues;
    const roundedPercentages = roundParityPercentages(percentages);
    const HOTEL_BAR_THRESHOLD = 5;

    return [
      {
        widthPercentage: roundedPercentages.loss * 100,
        backgroundColor: `var(${COLOR_PARITY_LOSS})`,
        isHidden:
          roundedPercentages.loss * 100 * this.scaler <= HOTEL_BAR_THRESHOLD,
      },
      {
        widthPercentage: roundedPercentages.meet * 100,
        backgroundColor: `var(${COLOR_PARITY_MEET})`,
        isHidden:
          roundedPercentages.meet * 100 * this.scaler <= HOTEL_BAR_THRESHOLD,
      },
      {
        widthPercentage: roundedPercentages.win * 100,
        backgroundColor: `var(${COLOR_PARITY_WIN})`,
        isHidden:
          roundedPercentages.win * 100 * this.scaler <= HOTEL_BAR_THRESHOLD,
      },
    ];
  }

  // Overrides segmentationValue displayName property
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get displayName() {
    const brandCode = this.args.data?.formattedBrandCode;

    return [
      this.args.data?.formattedSegmentationValue,
      brandCode ? `(${brandCode})` : undefined,
    ]
      .filter(Boolean)
      .join(' ');
  }

  get hotelViewQuery(): HotelViewFilters & { platform: Platform } {
    return {
      ...this.args.hotelViewFilters,
      platform: this.platformQueryParam,
    };
  }

  get isSelected(): boolean {
    let isSelected = false;

    if (this.args.parsedSegmentationFilters) {
      const parsedFilters = this.args.parsedSegmentationFilters;

      Object.keys(parsedFilters).forEach((filteredSegmentation) => {
        if (this.args.data?.segmentationId === filteredSegmentation) {
          // check if the current segmentation value is selected (by comparing segmentation_id & segmentation_value_id)
          if (
            // @ts-expect-error: Incorrect type
            parsedFilters[filteredSegmentation].indexOf(
              this.args.data?.segmentationValueId,
            ) !== -1
          ) {
            isSelected = true;
          }
        }
      });
    }

    return isSelected;
  }

  get platformQueryParam(): Platform {
    // Should only redirect if we have one single platform selected
    // We can only select one at a time right now but that will change once we support mobile app and mobile website
    if (this.args.parsedSegmentationFilters?.platform?.length === 1) {
      return this.args.parsedSegmentationFilters?.platform?.[0] as Platform;
    }
    return PLATFORMS.PLATFORM_DESKTOP;
  }

  @action
  // @ts-expect-error: Incorrect type
  segmentationFilterClicked(objSegmentationValue): void {
    if (this.args.data?.isSelectable) {
      if (this.isSelected) {
        this.willBeUnselected = true;
      } else {
        this.willBeSelected = true;
      }
      // Should only rely on segmentationId and segmentationValueId
      this.args.onSegmentationFilterClicked(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        objSegmentationValue.segmentationId,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        objSegmentationValue.segmentationValueId,
      );
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  trackHotelDetailViewOpened(hotelId): void {
    this.usageAnalytics.track('Hotel Details Explored', {
      eventTrigger: 'hotel-details-link',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      hotelId: parseInt(hotelId, 10),
    });
  }

  @action
  // @ts-expect-error: Incorrect type
  trackRedirectToHotelView(hotelId): void {
    this.usageAnalytics.track('Hotel View Opened', {
      eventTrigger: 'hotel-view-link',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      hotelId: parseInt(hotelId, 10),
    });
  }

  <template>
    {{! @glint-nocheck }}
    <div class="flex w-full" ...attributes>
      <button
        class="button--unstyled
          {{styles.button}}
          {{@data.segmentationId}}_value
          {{if this.showAsSelected styles.item--selected}}
          {{if @data.isSelectable styles.item--selectable}}"
        type="button"
        {{on
          "click"
          (preventDefault (fn this.segmentationFilterClicked @data))
        }}
        {{on "mouseenter" this.debouncedHoverTask.perform}}
        {{on "mouseleave" this.onMouseLeave}}
      >
        <PrismDataBar
          @data={{this.barData}}
          @thresholdWidthPercentage={{this.barThresholdWidthPercentage}}
          @valuePosition="bottom"
          as |DataBar|
        >
          <DataBar.label>
            <p class="text-200--regular truncate">
              {{this.displayName}}
            </p>
          </DataBar.label>

          <DataBar.value @text={{this.barValue}} />
        </PrismDataBar>
      </button>
      <div
        class="flex items-center justify-center truncate"
        data-event-id="market-parity-insight-live-view"
      >
        <LinkTo
          @model={{@data.segmentationValueId}}
          @query={{this.hotelViewQuery}}
          @route="hotel.parity.insight"
          class="text-300--regular redirect-to-hotel--minimal prism-link truncate"
          data-test-id="hotel-view-link"
          role="button"
          target="_blank"
          {{on
            "click"
            (fn this.trackRedirectToHotelView @data.segmentationValueId)
          }}
        >
          {{t "general.hotel_view"}}
        </LinkTo>
      </div>
      <div
        class="flex items-center justify-center truncate"
        data-event-id="market-parity-hotel-detail"
      >
        <LinkTo
          @query={{hash
            hotelId=@data.segmentationValueId
            platform=this.platformQueryParam
          }}
          @route="market.parity.insight.historical"
          class="text-300--regular redirect-to-hotel--minimal prism-link truncate"
          role="button"
          {{on
            "click"
            (fn this.trackHotelDetailViewOpened @data.segmentationValueId)
          }}
        >
          {{t "parity_analytics.hotels.historical"}}
        </LinkTo>
      </div>
    </div>
    {{#if this.isHovered}}
      <EmberTooltip
        @isShown={{this.isHovered}}
        @side="top"
        @tooltipClass="base-tooltip parity-analytics-segmentation-value-tooltip"
      >
        <ParityTooltipsTooltipKpi
          @data={{this.barValues}}
          @displayName={{this.displayName}}
          @hideShoppedHotels={{true}}
          @splitBenchmark={{true}}
        />
      </EmberTooltip>
    {{/if}}
  </template>
}
